import moment from 'moment'

export const formatRevenue = val => {
  const n = +val || 0
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`
  if (n >= 1000)   return `₹${(n / 1000).toFixed(1)}k`
  return `₹${n.toLocaleString('en-IN')}`
}

export const formatMonth = val =>
  val ? moment(val, 'YYYY-MM').format('MMM YYYY') : val

export const conversionRate = item => {
  const sl = +item.shortlisted_count || 0
  const jo = +item.joiners_count     || 0
  return sl ? Math.round((jo / sl) * 100) : 0
}

export const conversionClass = item => {
  const r = conversionRate(item)
  if (r >= 30) return 'text-success font-weight-bold'
  if (r >= 15) return 'text-warning font-weight-bold'
  return 'text-danger'
}

export const pageInfo = (total, currentPage, perPage) => {
  if (!total) return 'No records'
  const from = (currentPage - 1) * perPage + 1
  const to   = Math.min(currentPage * perPage, total)
  return `Showing ${from}–${to} of ${total}`
}

export const isInfraVertical = v =>
  String(v || '').toLowerCase().includes('infra')

export const isServiceVertical = v =>
  String(v || '').toLowerCase().includes('service')

export const verticalShortLabel = v => {
  if (isInfraVertical(v)) return 'Infra'
  if (isServiceVertical(v)) return 'Service'
  return v || '—'
}

/** Existing category/collar keys only — do not invent API fields. */
export const COLLAR_FIELD_KEYS = [
  'job_category',
  'category',
  'collar',
  'job_collar',
  'Category',
  'JobCategory',
]

export const readCollarField = row => {
  if (!row) return null
  for (let i = 0; i < COLLAR_FIELD_KEYS.length; i += 1) {
    const raw = row[COLLAR_FIELD_KEYS[i]]
    if (raw != null && String(raw).trim() !== '') return String(raw).trim()
  }
  return null
}

export const normalizeCollar = value => {
  if (!value) return null
  const s = String(value).toLowerCase()
  if (s.includes('blue')) return 'Blue Collar'
  if (s.includes('white')) return 'White Collar'
  return null
}

/** True when any row already carries a category/collar value from the API. */
export const hasPerformanceCollarData = rows =>
  (rows || []).some(r => normalizeCollar(readCollarField(r)))

/**
 * Service is White collar only (product rule).
 * Infra Blue/White only when a category/collar field is present on the row.
 */
export const resolvePerformanceCollar = row => {
  if (!row) return null
  if (isServiceVertical(row.job_vertical)) return 'White Collar'
  return normalizeCollar(readCollarField(row))
}

const emptyMix = () => ({
  infraPs: 0,
  servicePs: 0,
  infraBluePs: 0,
  infraWhitePs: 0,
  infraPct: 0,
  servicePct: 0,
  infraBluePct: 0,
  infraWhitePct: 0,
})

const finalizeMix = m => {
  const total = m.infraPs + m.servicePs
  m.infraPct = total ? Math.round((m.infraPs / total) * 100) : 0
  m.servicePct = total ? 100 - m.infraPct : 0
  const collarTotal = m.infraBluePs + m.infraWhitePs
  m.infraBluePct = collarTotal ? Math.round((m.infraBluePs / collarTotal) * 100) : 0
  m.infraWhitePct = collarTotal ? 100 - m.infraBluePct : 0
  return m
}

/** Per-recruiter Infra/Service PS mix, plus Infra collar split when the API sends it. */
export const buildRecruiterWorkload = (aggRows, rawRows) => {
  const mix = {}
  const ensure = empId => {
    if (!mix[empId]) mix[empId] = emptyMix()
    return mix[empId]
  }

  ;(aggRows || []).forEach(r => {
    const m = ensure(r.emp_id)
    const ps = +r.profile_shared_count || 0
    if (isInfraVertical(r.job_vertical)) m.infraPs += ps
    else if (isServiceVertical(r.job_vertical)) m.servicePs += ps
  })

  if (hasPerformanceCollarData(rawRows)) {
    ;(rawRows || []).forEach(r => {
      if (!isInfraVertical(r.job_vertical)) return
      const collar = normalizeCollar(readCollarField(r))
      if (!collar) return
      const m = ensure(r.emp_id)
      const ps = +r.profile_shared_count || 0
      if (collar === 'Blue Collar') m.infraBluePs += ps
      else m.infraWhitePs += ps
    })
  }

  Object.keys(mix).forEach(id => finalizeMix(mix[id]))
  return mix
}

export const buildVerticalWorkloadSummary = (aggRows, rawRows) => {
  const mixByEmp = buildRecruiterWorkload(aggRows, rawRows)
  let infraPs = 0
  let servicePs = 0
  let infraOnly = 0
  let serviceOnly = 0
  let both = 0
  let infraBluePs = 0
  let infraWhitePs = 0

  Object.keys(mixByEmp).forEach(id => {
    const m = mixByEmp[id]
    infraPs += m.infraPs
    servicePs += m.servicePs
    infraBluePs += m.infraBluePs
    infraWhitePs += m.infraWhitePs
    if (m.infraPs && m.servicePs) both += 1
    else if (m.infraPs) infraOnly += 1
    else if (m.servicePs) serviceOnly += 1
  })

  const totals = finalizeMix({
    ...emptyMix(),
    infraPs,
    servicePs,
    infraBluePs,
    infraWhitePs,
  })

  return {
    ...totals,
    infraOnly,
    serviceOnly,
    both,
    recruiterCount: Object.keys(mixByEmp).length,
    hasCollar: hasPerformanceCollarData(rawRows),
  }
}
